import { FC, ReactNode } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import classNames from 'classnames';

import { sanitizeId } from 'common/utils/sanitizeUtils';

export interface JabraRadioGroupProps {
  title?: string;
  children: ReactNode;
  selected?: string;
  className?: string;
  onValueChange?: (value: string) => void;
}

export const JabraRadioGroup: FC<JabraRadioGroupProps> = ({
  title,
  children,
  className,
  selected,
  onValueChange,
}) => {
  const handleValueChange = (value: string) => {
    onValueChange && onValueChange(value);
  };
  return (
    <form role="presentation">
      <RadioGroup.Root
        loop={false}
        onValueChange={handleValueChange}
        value={selected}
        aria-label={title}
        className={className}
      >
        {children}
      </RadioGroup.Root>
    </form>
  );
};

export interface JabraRadioButtonProps {
  value: string;
  title: string;
  disabled?: boolean;
  theme?: JabraRadioButtonTheme;
  selectedStyle?: JabraRadioButtonStyles;
}

export enum JabraRadioButtonTheme {
  light = 'light',
  dark = 'dark',
}

export enum JabraRadioButtonStyles {
  default = 'default',
  primary = 'primary',
}

export const JabraRadioButton: FC<JabraRadioButtonProps> = ({
  title,
  value,
  disabled,
  theme = JabraRadioButtonTheme.light,
  selectedStyle = JabraRadioButtonStyles.default,
}) => {
  const groupId = sanitizeId(`radio-group-${title}-${value}`);
  const isPrimarySelected = selectedStyle === JabraRadioButtonStyles.primary;

  return (
    <div
      className="align-center my-1 flex items-center data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-40"
      data-disabled={disabled}
    >
      <RadioGroup.Item
        id={groupId}
        role="radio"
        disabled={disabled}
        className={classNames(
          'border-slate hover:border-beige-shade90 group mr-2 h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none',
          {
            'border-primary': theme === JabraRadioButtonTheme.dark,
          }
        )}
        value={value}
      >
        <RadioGroup.Indicator className="leading-0 flex items-center justify-center">
          <div
            className={classNames(
              'h-[0.9rem] w-[0.9rem] rounded-full',
              isPrimarySelected || theme === JabraRadioButtonTheme.dark
                ? 'bg-primary'
                : 'bg-gray-700 group-hover:bg-black'
            )}
          ></div>
        </RadioGroup.Indicator>
      </RadioGroup.Item>
      <label
        className="min-w-2 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-sm"
        htmlFor={groupId}
      >
        {title}
      </label>
    </div>
  );
};
